import React, {
  createContext,
  useState,
  useEffect,
  useCallback,
  useContext,
} from 'react'
import {useQuery, useMutation, useQueryClient} from 'react-query'
import {
  getUserBusinesses,
  createBusiness as createBusinessApi,
  setPrimaryBusiness as setPrimaryBusinessApi,
} from '../api'
import {
  getSelectedBusinessId,
  setSelectedBusinessId,
  getItemFromStorage,
} from '../utils/storage'
import {AuthContext} from './AuthContext'

export const BusinessContext = createContext()

export const BusinessProvider = ({children}) => {
  const [businesses, setBusinesses] = useState([])
  const [selectedBusiness, setSelectedBusiness] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [currentUserId, setCurrentUserId] = useState(null)

  const queryClient = useQueryClient()
  const {state: authState} = useContext(AuthContext)

  // Derive user from AuthContext (reactive) with localStorage fallback
  const user = authState?.user || getItemFromStorage({name: 'user'})

  // Monitor for user changes and clear state when user changes
  useEffect(() => {
    if (user?.id !== currentUserId) {
      console.log('User changed from', currentUserId, 'to', user?.id)
      // User has changed, clear all business data
      setBusinesses([])
      setSelectedBusiness(null)
      setCurrentUserId(user?.id)
      // Clear React Query cache for the old user
      if (currentUserId) {
        queryClient.removeQueries(['userBusinesses', currentUserId])
      }
    }
  }, [user?.id, currentUserId, queryClient])

  // Fetch user's businesses
  const {data: businessesData, isLoading: businessesLoading} = useQuery(
    ['userBusinesses', user?.id],
    () => getUserBusinesses(user?.id),
    {
      enabled: !!user?.id,
      onSuccess: (data) => {
        console.log(
          'Fetched businesses for user',
          user?.id,
          ':',
          data.businesses
        )
        setBusinesses(data.businesses || [])
      },
      onError: (error) => {
        console.error('Error fetching businesses:', error)
        setBusinesses([])
      },
    }
  )

  // Everyone acts as themselves until they pick one of their businesses in
  // the switcher; that choice is remembered.
  useEffect(() => {
    if (businesses.length > 0) {
      const storedBusinessId = getSelectedBusinessId()
      const stored = storedBusinessId
        ? businesses.find((b) => b.id === storedBusinessId)
        : null
      if (stored) {
        setSelectedBusiness(stored)
      } else {
        setSelectedBusiness(null)
        if (storedBusinessId) setSelectedBusinessId(null)
      }
    }

    // Only mark as loaded once the query has finished
    if (!businessesLoading) {
      setIsLoading(false)
    }
  }, [businesses, businessesLoading])

  // Switch to a different business
  const switchBusiness = useCallback(
    (businessId) => {
      const business = businesses.find((b) => b.id === businessId)
      if (business) {
        setSelectedBusiness(business)
        setSelectedBusinessId(businessId)
      } else {
        // Business not in array yet (e.g., just created), but store ID for later
        // When businesses load, the useEffect will find this ID and select it
        console.log(
          'Business not found in array yet, storing ID for later:',
          businessId
        )
        setSelectedBusinessId(businessId)
      }
    },
    [businesses]
  )

  // Create new business mutation
  const createBusinessMutation = useMutation(
    (businessData) => createBusinessApi(businessData),
    {
      onSuccess: (newBusiness) => {
        // Refresh businesses list
        queryClient.invalidateQueries(['userBusinesses', user?.id])

        // Adding a business doesn't switch to it; the switcher does.
        setBusinesses((prev) => [...prev, newBusiness])
      },
      onError: (error) => {
        console.error('Error creating business:', error)
      },
    }
  )

  // Refresh businesses list
  const refreshBusinesses = useCallback(() => {
    queryClient.invalidateQueries(['userBusinesses', user?.id])
  }, [queryClient, user?.id])

  // Set primary business
  const setPrimaryBusinessFunc = useCallback(
    async (businessId) => {
      try {
        await setPrimaryBusinessApi(user?.id, businessId)
        await refreshBusinesses()
      } catch (error) {
        console.error('Error setting primary business:', error)
      }
    },
    [user?.id, refreshBusinesses]
  )

  // Back to acting as yourself.
  const clearSelectedBusiness = useCallback(() => {
    setSelectedBusiness(null)
    setSelectedBusinessId(null)
  }, [])

  const value = {
    businesses,
    selectedBusiness,
    isLoading,
    switchBusiness,
    clearSelectedBusiness,
    createBusiness: createBusinessMutation.mutate,
    // Resolves with the new business, for callers that open it next.
    createBusinessAsync: createBusinessMutation.mutateAsync,
    isCreatingBusiness: createBusinessMutation.isLoading,
    refreshBusinesses,
    setPrimaryBusiness: setPrimaryBusinessFunc,
    // Computed values
    hasBusinesses: businesses.length > 0,
    // Anyone signed in can add a business (a professional who also owns a
    // salon, say); it doesn't change who they are elsewhere.
    canCreateBusiness: Boolean(user?.id),
  }

  return (
    <BusinessContext.Provider value={value}>
      {children}
    </BusinessContext.Provider>
  )
}
